/**
 * DrawerHeader — the CardDetailDrawer header: inline-editable title, the
 * status/meta line (Mark done, status pill, lane, "updated …", the
 * Saving/Saved/Failed save indicator), and the merge / pin / close icon
 * buttons.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Behaviour is unchanged — every handler is a 1:1 move; state + handlers
 * arrive via the `editing` / `data` / `layout` hook objects.
 */
import { CheckSquare, Maximize2, Merge, PanelRight, Pin, PinOff, RotateCcw, X } from 'lucide-react'
import type { KanbanApi } from '../../../api'
import type { Card, CardStatus, Lane } from '../../../types'
import { STATUS_META, STATUS_OPTIONS } from '../drawerChrome'
import type { KanbanOpenMode } from '../openModePref'
import { formatDateTime } from '../shared'
import type { DrawerData, DrawerEditing, DrawerLayout } from '../drawerHookTypes'

export interface DrawerHeaderProps {
  card: Card
  currentLane: Lane | undefined
  inlineMode: boolean
  api: KanbanApi
  boardCards?: Card[]
  editing: DrawerEditing
  data: DrawerData
  layout: DrawerLayout
  /** ARC-1 MR5 (U14) — per-user open-mode pref + its setter. Omitted by
   *  hosts that predate the pref (toggle hides). */
  openMode?: KanbanOpenMode
  setOpenMode?: (next: KanbanOpenMode) => void
}

export function DrawerHeader({
  card,
  currentLane,
  inlineMode,
  api,
  boardCards,
  editing,
  data,
  layout,
  openMode,
  setOpenMode,
}: DrawerHeaderProps) {
  const {
    titleRef, title, setTitle, dirtyFieldsRef, patchCard,
    status, setStatus, busyKey, error, offlineSave, hasFailedSave,
    retryLastSave, savedAt, closeWithFlush,
  } = editing
  const { setMergeDialogOpen } = data
  const { pinned, setPinned } = layout

  return (
    <header className="flex items-start gap-2 border-b border-neutral-200 dark:border-neutral-800 pl-5 pr-4 py-3">
      <div className="min-w-0 flex-1">
        {/* Inline-editable title — a multi-line auto-growing textarea so a
            long title wraps and stays FULLY visible. Enter commits (blur)
            rather than inserting a newline. */}
        <textarea
          ref={titleRef}
          rows={1}
          value={title}
          onChange={(e) => {
            dirtyFieldsRef.current.add('title')
            setTitle(e.target.value)
          }}
          onBlur={() => {
            if (title.trim() && title.trim() !== card.title) {
              void patchCard('title', { title: title.trim() })
            } else if (!title.trim()) {
              setTitle(card.title)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              ;(e.target as HTMLTextAreaElement).blur()
            }
            if (e.key === 'Escape') {
              setTitle(card.title)
              ;(e.target as HTMLTextAreaElement).blur()
            }
          }}
          className="block w-full resize-none overflow-hidden break-words bg-transparent text-[17px] font-semibold leading-snug tracking-tight text-neutral-900 dark:text-neutral-100 focus:outline-none focus:bg-neutral-100 dark:focus:bg-neutral-900 rounded-md px-1.5 -mx-1.5 py-0.5"
        />
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-neutral-500">
          {/* Quick "Mark done" toggle — lives on the status/meta line. Writes
              through the SAME patchCard('status') path as the dropdown. */}
          {status !== 'ARCHIVED' && (
            <button
              type="button"
              onClick={() => {
                // BUG-2026-06-29-KANBAN-MARKDONE-OVERWRITES-STATUS — read the
                // LOCAL status (what the operator currently sees), not the
                // server echo `card.status`, which lags a freshly-set status.
                const next: CardStatus = status === 'DONE' ? 'OPEN' : 'DONE'
                setStatus(next)
                void patchCard('status', { status: next } as never)
              }}
              disabled={busyKey === 'status'}
              aria-pressed={status === 'DONE'}
              aria-label={status === 'DONE' ? 'Reopen card' : 'Mark card as done'}
              title={status === 'DONE' ? 'Done — click to reopen' : 'Mark as done'}
              className={
                status === 'DONE'
                  ? 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                  : 'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium border border-emerald-600/40 text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:border-emerald-500/40 dark:hover:bg-emerald-950/40 disabled:opacity-50'
              }
            >
              <CheckSquare className="h-4 w-4" />
              {status === 'DONE' ? 'Done' : 'Mark done'}
            </button>
          )}
          {/* Status pill — reads card state at a glance; the editable control
              lives in the property list below. Tracks live `status`. */}
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-medium ${STATUS_META[status]?.text ?? 'text-neutral-600 dark:text-neutral-300'}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${STATUS_META[status]?.dot ?? 'bg-neutral-400'}`} />
            {STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status}
          </span>
          {currentLane && (
            <>
              <span className="text-neutral-300 dark:text-neutral-600">·</span>
              <span className="rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5">
                in <span className="font-medium text-neutral-700 dark:text-neutral-200">{currentLane.name}</span>
              </span>
            </>
          )}
          <span className="text-neutral-300 dark:text-neutral-600">·</span>
          <span>updated {formatDateTime(card.updated_at)}</span>
          {/* Audit Arc 3 — drawer save indicator. Three mutually-exclusive
              variants sharing the meta-row: Saving… / Save failed / Saved. */}
          {busyKey && (
            <>
              <span>·</span>
              <span
                className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"
                title={`Saving ${busyKey}…`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Saving…
              </span>
            </>
          )}
          {!busyKey && error && (
            <>
              <span>·</span>
              <span
                className={[
                  'inline-flex items-center gap-1',
                  offlineSave
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400',
                ].join(' ')}
                title={error}
              >
                <span
                  className={[
                    'h-1.5 w-1.5 rounded-full',
                    offlineSave ? 'bg-amber-500' : 'bg-red-500',
                  ].join(' ')}
                />
                {offlineSave ? 'Offline — will retry' : 'Save failed'}
              </span>
              {hasFailedSave && (
                <button
                  type="button"
                  onClick={retryLastSave}
                  title={error || 'Retry the last failed save'}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30"
                >
                  <RotateCcw className="h-3 w-3" /> Retry
                </button>
              )}
            </>
          )}
          {!busyKey && !error && savedAt && Date.now() - savedAt < 4000 && (
            <>
              <span>·</span>
              <span
                className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"
                title="All edits saved"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Saved
              </span>
            </>
          )}
        </div>
      </div>
      {/* Merge into… (item 13) — visible only when the host wired both the
          merge endpoint AND passed boardCards. */}
      {api.mergeCards && boardCards && boardCards.length > 1 && (
        <button
          type="button"
          onClick={() => setMergeDialogOpen(true)}
          aria-label="Merge another card into this one"
          title="Merge another card into this one"
          className="inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
        >
          <Merge className="h-4 w-4" />
        </button>
      )}
      {/* ARC-1 MR5 (U14) — open-mode toggle: drawer ⇄ Trello-style popup.
          Persisted per user (kanban:prefs:<userId>); every mounted drawer
          re-chromes immediately. Rendered in EVERY mode so an operator can
          always get back out of the popup. */}
      {openMode && setOpenMode && (
        <button
          type="button"
          data-testid="drawer-open-mode-toggle"
          onClick={() => setOpenMode(openMode === 'popup' ? 'drawer' : 'popup')}
          aria-pressed={openMode === 'popup'}
          aria-label={
            openMode === 'popup'
              ? 'Open cards in the side drawer'
              : 'Open cards as a full-screen popup'
          }
          title={
            openMode === 'popup'
              ? 'Switch to drawer mode (persisted)'
              : 'Switch to full-screen popup mode (persisted)'
          }
          className={
            openMode === 'popup'
              ? 'inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40'
              : 'inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200'
          }
        >
          {openMode === 'popup' ? <PanelRight className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      )}
      {/* Pin/unpin toggle — suppressed in inline mode (the rail container
          owns the docked-vs-overlay decision) and in popup mode (a modal
          is never pinned). */}
      {!inlineMode && !layout.popup && (
        <button
          type="button"
          onClick={() => setPinned(!pinned)}
          aria-label={pinned ? 'Unpin (return to overlay mode)' : 'Pin as split pane'}
          aria-pressed={pinned}
          title={pinned ? 'Unpin — return to overlay mode' : 'Pin as split pane (persisted)'}
          className={
            pinned
              ? 'inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/40'
              : 'inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200'
          }
        >
          {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
        </button>
      )}
      <button
        type="button"
        onClick={() => void closeWithFlush()}
        aria-label="Close"
        className="inline-flex items-center justify-center rounded p-1 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200"
      >
        <X className="h-4 w-4" />
      </button>
    </header>
  )
}
